// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartContent.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import ReportPartStyleFactories from 'share/factories/Izenda.ReportPartStyleFactories';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { getConfiguration } from 'share/factories/Izenda.ReportPartStyleConfigurationFactories';
import { get, compose, has, find, flow, filter, map, includes, first } from 'lodash/fp';
import { exportClass } from 'share/Izenda.ServiceProvider';
import { observable, extendObservable, action } from 'mobx';
import { getAll } from 'share/factories/Izenda.ReportPartStyleFactories';

@exportClass('ReportPartContent')
export default class ReportPartContent {
  constructor(type: string) {
    this.type = type;
    this.optionsDefaultFunc = (style, vizEngine) =>
      compose(
        get('optionsDefault'),
        getConfiguration
      )(type, style, vizEngine);

    this.isChartType = ReportPartStyleFactories.isReportPartStyle(this.type);
    this.getBaseChartType = ReportPartStyleFactories.getBaseStyle(this.type);
  }

  @observable customContainers = [];

  /**
   * Determine whether the report part content automatically sizes its container or not
   */
  get autoSize() {
    return false;
  }

  get optionsDefault() {
    return this.optionsDefaultFunc(this.reportPartStyle, this.vizEngine);
  }

  get defaultOptionsByType() {
    return this.optionsDefault.optionsByType;
  }

  get defaultCommonOptions() {
    return this.optionsDefault.commonOptions;
  }

  get reportPartStyle() {
    throw new Error('The report part content has not defined report part style');
  }

  get vizEngine() {
    throw new Error('The report part content has not defined visualization engine');
  }

  get defaultVizEngine() {
    throw new Error('The report part content has not defined default visualization engine');
  }

  setVizEngine(value: string) {
    throw new Error('This report part content has not implemented the set visualization engine function');
  }

  /**
   * Set the viz engine by current chart type
   */
  @action
  setVizEngineByCurrentChartType() {
    const matchedVizEngines = flow(
      filter({ style: this.reportPartStyle }),
      map('vizEngine')
    )(getAll(this.type));
    const hasDefaultVizEngine = includes(this.defaultVizEngine, matchedVizEngines);
    this.setVizEngine(hasDefaultVizEngine ? this.defaultVizEngine : first(matchedVizEngines));
  }

  /**
   * Add a custom field container
   * @param {string} containerName The custom container name
   */
  addCustomContainer(containerName) {
    ensure(containerName, 'Container name must have a value');
    ensure(!has(containerName, this), `The container name ${containerName} has been used.`);

    const newCustomContainer = ReportPartUtils.addCustomContainers(this.customContainers, containerName);

    extendObservable(this, {
      [containerName]: newCustomContainer
    });
  }

  @action('readData')
  readData(content: any): void {
    content = content || {};

    const customContainersContent = get('customContainers', content);

    if (customContainersContent && customContainersContent.length > 0) {
      const newCustomContainers = this.customContainers.map(container => {
        const { name: containerName } = container;
        const customContent = find(['name', containerName], customContainersContent);
        return customContent ? ReportPartUtils.populateReportChartContainer(customContent, containerName) : container;
      });

      this.customContainers.replace(newCustomContainers);
    }
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartContent.js